import { Button } from "#scripts/components/ui/button.tsx";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { ClickToSourceDemo } from "./click-to-source-demo.tsx";

const BUTTON_SOURCE = `import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import type { Ta } from "#scripts/components/ui/icons.tsx";
import { cn } from "#scripts/lib/utils.ts";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap user-select-none",
    "text-xs font-medium font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "border border-transparent",
  ],
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-input bg-background hover:bg-bg-elevated hover:text-text",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        selected: "border-border bg-bg-elevated text-text",
        ghost: "hover:bg-border/30 hover:text-text",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-6 px-1 gap-1",
        sm: "h-7 px-3 gap-1",
        default: "h-8 px-4 gap-1.5",
        lg: "h-10 px-5 gap-2",
        icon: "h-8 w-8",
      },
      rounded: {
        default: "rounded-full",
        rounded: "rounded-full",
        squarish: "rounded-md",
        groupEnd: "rounded-full border-l-0 rounded-l-none pl-3",
        groupStart: "rounded-full rounded-r-none pr-3",
        groupMiddle: "rounded-none border-l-0 border-r",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
      rounded: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  IconStart?: Ta.TablerIcon;
  IconEnd?: Ta.TablerIcon;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, IconStart, IconEnd, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, rounded, className }))} ref={ref} {...props}>
        {IconStart && (
          <IconStart aria-hidden="true" data-loc="phosphor-blog/scripts/components/demo/click-to-source-example.tsx:68:41" data-name="IconStart" className="w-4 h-4 stroke-[1.25]" />
        )}
        {children}
        {IconEnd && (
          <IconEnd aria-hidden="true" data-loc="phosphor-blog/scripts/components/demo/click-to-source-example.tsx:72:39" data-name="IconEnd" className="w-4 h-4 stroke-[1.25]" />
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };`;

const CARD_SOURCE = `export function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4 bg-bg-elevated border border-border rounded-md">
      {children}
    </div>
  );
}`;

const fileMap = {
  "scripts/components/ui/button.tsx": BUTTON_SOURCE,
  "scripts/components/ui/card.tsx": CARD_SOURCE,
};

export function ClickToSourceExample() {
  return (
    <ClickToSourceDemo fileMap={fileMap}>
      <div className="space-y-4">
        <div
          data-loc="scripts/components/ui/card.tsx:1:1"
          data-name="Card"
          className="p-4 bg-bg-elevated border border-border rounded-md"
        >
          <h3 className="text-sm font-medium mb-3 text-text">Interactive Demo</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              data-loc="scripts/components/ui/button.tsx:58:7"
              data-name="Button"
              variant="primary"
              size="sm"
              IconStart={Ta.IconPlus}
            >
              Add Item
            </Button>
            <Button
              data-loc="scripts/components/ui/button.tsx:58:7"
              data-name="Button"
              variant="outline"
              size="sm"
              IconStart={Ta.IconEdit}
            >
              Edit
            </Button>
            <Button
              data-loc="scripts/components/ui/button.tsx:58:7"
              data-name="Button"
              variant="destructive"
              size="sm"
              IconStart={Ta.IconTrash}
            >
              Delete
            </Button>
            <Button
              data-loc="scripts/components/ui/button.tsx:58:7"
              data-name="Button"
              variant="ghost"
              size="icon"
              IconStart={Ta.IconSettings}
            />
          </div>
        </div>
      </div>
    </ClickToSourceDemo>
  );
}
